import { Button, Checkbox, Table, Tooltip, Typography } from "antd"
import Layout from "modules/layout/dashboard"
import { useActivityId } from "../.."
import { useExtEvents } from "../queries"
import { useMemberId } from "pages/member"
import { useEffect, useState } from "react"
import { TableRowSelection } from "antd/es/table/interface"
import { IExtEvent } from "../types"
import Loader from "components/loader"
import { useTranslation } from "react-i18next"
import { useMember } from "../../../queries"
import { useAddToCart } from "pages/cart/queries"
import { useSearchParams } from "react-router-dom"
import { SESSION_PARAM } from ".."

const { Main } = Layout
const { Title } = Typography

const ShowActivity: React.FC = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const memberId = useMemberId()
  const member = useMember(memberId)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const activityId = useActivityId()

  const events = useExtEvents(
    {
      filter: {
        session: { values: [searchParams.get(SESSION_PARAM)], operator: "in" },
        activity: { values: [activityId], operator: "in" },
        status: { values: ["draft", "approved"], operator: "in" },
      },

      check_cart_for_member_id: memberId,
    },
    {
      enabled: !!searchParams.get(SESSION_PARAM),
      select(data) {
        return data.map((e) => ({ ...e, activity_id: activityId! }))
      },
    }
  )

  const addToCart = useAddToCart(memberId!, {
    meta: {
      type: "add",
      context: "female",
      count: 1,
      entity: t("events.presubscription"),
    },
  })

  useEffect(() => {
    if (events.data) {
      const selected = events
        .data!.map((e) => e.requested_member_is_already_in_cart && e.id)
        .filter(Boolean)
      setSelectedRowKeys(selected as any)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.data, events.isSuccess])

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const toggleSelectAll = () => {
    setSelectedRowKeys((keys) =>
      keys.length === events.data?.length
        ? events
            .data!.filter((e) => e.requested_member_is_already_in_cart)
            .map((e) => e.id)
        : events.data!.map((e) => e.id)
    )
  }

  const headerCheckbox = (
    <Checkbox
      checked={selectedRowKeys.length === events.data?.length}
      indeterminate={
        selectedRowKeys.length > 0 &&
        selectedRowKeys.length !== events.data?.length
      }
      onChange={toggleSelectAll}
    />
  )

  const rowSelection: TableRowSelection<IExtEvent> = {
    selectedRowKeys,
    onChange: onSelectChange,
    columnTitle: headerCheckbox,

    getCheckboxProps(record) {
      const disabled = record.requested_member_is_already_in_cart
      return {
        disabled,
      }
    },
  }

  const handlePresubscribe = () =>
    addToCart.mutate({
      events: events.data
        ?.filter(
          (event) =>
            selectedRowKeys.includes(event.id) &&
            !event.requested_member_is_already_in_cart
        )
        .map((event) => event.id)!,
      apply_to_similar_events: false,
    })

  let activityName = events?.data && events?.data[0]?.name
  return (
    <>
      <Main.PageHeader
        breadcrumbs={[
          {
            title: member.data?.name,
            link: `/members/${member.data?.id}/events`,
          },
          {
            title: t("events.presubscriptions"),
            link: `/members/${member.data?.id}/events/registration`,
          },
          { title: activityName },
        ]}
        title={<Title level={3}>{activityName}</Title>}
        rightContent={
          <Button
            disabled={
              !events.data
                ?.filter((e) => !e.requested_member_is_already_in_cart)
                .some((e) => selectedRowKeys.includes(e.id))
            }
            loading={addToCart.isLoading}
            onClick={handlePresubscribe}
          >
            {t("events.add_to_cart")}
          </Button>
        }
      />
      {events.isLoading ? (
        <Loader />
      ) : (
        <>
          <Table
            tableLayout="fixed"
            pagination={false}
            rowKey={"id"}
            rowSelection={rowSelection}
            dataSource={events.data}
            columns={[
              {
                title: t<string>("events.start_date"),
                dataIndex: "start_date",
                key: "start_date",
                ellipsis: true,

                render: (value) => (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                ),
              },
              {
                title: t<string>("events.end_date"),
                dataIndex: "end_date",
                key: "end_date",
                ellipsis: true,
                render: (value) => (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                ),
              },
              {
                title: t<string>("events.start_hour"),
                dataIndex: "start_hour",
                key: "start_hour",
                ellipsis: true,
                render: (value) => (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                ),
              },
              {
                title: t<string>("events.end_hour"),
                dataIndex: "end_hour",
                key: "end_hour",
                ellipsis: true,
                render: (value) => (
                  <Tooltip placement="bottomLeft" title={value}>
                    {value}
                  </Tooltip>
                ),
              },
            ]}
          />
        </>
      )}
    </>
  )
}

export default ShowActivity
